import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { SettingsContext } from '../contexts/SettingsContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSliders, faKey } from '@fortawesome/free-solid-svg-icons';

const ModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

const ModalContent = styled.div`
  background: #ffffff;
  color: var(--text-color);
  padding: 0;
  border-radius: var(--radius-xl);
  width: 90%;
  max-width: 650px;
  max-height: 90vh;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05);
  position: relative;
  direction: rtl;
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .dark-mode & {
    background: var(--neutral-800);
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-5) var(--space-6);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: #ffffff;
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;

  .dark-mode & {
    background: var(--neutral-800);
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: var(--primary-700);
  font-size: var(--text-2xl);
  font-weight: 600;
  font-family: var(--font-heading-bold);
  display: flex;
  align-items: center;
  gap: var(--space-3);

  .dark-mode & {
    color: var(--primary-300);
  }
`;

const CloseButton = styled.button`
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: var(--radius-lg);
  width: 40px;
  height: 40px;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: var(--text-base);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  &:hover {
    background: var(--error-50);
    color: var(--error-600);
    border-color: var(--error-300);
    transform: scale(1.05);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  .dark-mode & {
    background: var(--neutral-700);
    border-color: var(--border-color);
    color: var(--text-secondary);
  }
`;

const TabContainer = styled.div`
  display: flex;
  padding: 0 var(--space-6);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: #ffffff;

  .dark-mode & {
    background: var(--neutral-800);
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }
`;

const Tab = styled.button`
  padding: var(--space-4) var(--space-6);
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  color: var(--text-secondary);
  font-size: var(--text-base);
  font-weight: 500;
  font-family: var(--font-family);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: var(--space-2);
  position: relative;

  &:hover {
    color: var(--primary-600);
    background: var(--primary-50);
  }

  &.active {
    color: var(--primary-600);
    border-bottom-color: var(--primary-600);
    background: var(--primary-50);

    .dark-mode & {
      background: var(--neutral-700);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: var(--space-6);
  background: #ffffff;

  .dark-mode & {
    background: var(--neutral-800);
  }
`;

const TabContent = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
`;

const ModalFooter = styled.div`
  padding: var(--space-5) var(--space-6);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #ffffff;
  border-radius: 0 0 var(--radius-xl) var(--radius-xl);
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);

  .dark-mode & {
    background: var(--neutral-800);
    border-top-color: rgba(255, 255, 255, 0.1);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  font-weight: 500;
  font-size: var(--text-base);
  color: var(--text-color);
  font-family: var(--font-family);
  font-weight: 400;
`;

const Input = styled.input`
  padding: var(--space-3) var(--space-4);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: var(--radius-lg);
  background-color: #ffffff;
  color: var(--text-color);
  font-size: var(--text-base);
  font-family: var(--font-family);
  font-weight: 400;
  direction: ${props => props.type === 'password' ? 'ltr' : 'rtl'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100), 0 1px 3px rgba(0, 0, 0, 0.04);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100), 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  &:hover:not(:focus) {
    border-color: var(--primary-300);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }

  .dark-mode & {
    background-color: var(--neutral-700);
    border-color: var(--border-color);
    box-shadow: var(--shadow-sm);
  }
`;

const Select = styled.select`
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  background-color: var(--surface-color);
  color: var(--text-color);
  font-size: 0.875rem;
  font-family: var(--font-family);
  font-weight: 400;
  direction: rtl;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100);
  }
`;

const TextArea = styled.textarea`
  padding: var(--space-3) var(--space-4);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: var(--radius-lg);
  background-color: #ffffff;
  color: var(--text-color);
  font-size: var(--text-base);
  font-family: var(--font-family);
  font-weight: 400;
  resize: vertical;
  min-height: 120px;
  direction: rtl;
  line-height: 1.6;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100), 0 1px 3px rgba(0, 0, 0, 0.04);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100), 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  &:hover:not(:focus) {
    border-color: var(--primary-300);
  }

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
  }

  .dark-mode & {
    background-color: var(--neutral-700);
    border-color: var(--border-color);
    box-shadow: var(--shadow-sm);
  }
`;

const Button = styled.button`
  padding: var(--space-3) var(--space-5);
  background: linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-weight: 500;
  font-family: var(--font-family);
  font-weight: 400;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%);
    border-radius: inherit;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    background: linear-gradient(135deg, var(--primary-700) 0%, var(--primary-800) 100%);
    box-shadow: 0 6px 20px rgba(90, 99, 230, 0.4);
    transform: translateY(-2px);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
  margin-right: var(--space-3);
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: var(--primary-600);
  }

  &:checked + span:before {
    transform: translateX(-20px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--neutral-300);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: var(--radius-full);
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    right: 3px;
    bottom: 3px;
    background-color: white;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: var(--radius-full);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  .dark-mode & {
    background-color: var(--neutral-600);
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 var(--space-4) 0;
  color: var(--text-color);
  font-size: 1.125rem;
  font-weight: 600;
  font-family: var(--font-family);
  font-weight: 400;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--border-color-light);

  &:last-child {
    border-bottom: none;
  }
`;

const ApiKeySection = styled.div`
  display: ${props => props.show ? 'block' : 'none'};
  margin-top: var(--space-4);
  padding: var(--space-4);
  background-color: #f8f9fa;
  border-radius: var(--radius-lg);
  border: 1px solid rgba(0, 0, 0, 0.08);

  .dark-mode & {
    background-color: var(--neutral-700);
    border-color: var(--border-color);
  }
`;

function SettingsModal({ isOpen, onClose }) {
  const { settings, setSettings } = useContext(SettingsContext);

  // Local state for form
  const [tempSettings, setTempSettings] = useState(settings);
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [anthropicApiKey, setAnthropicApiKey] = useState('');
  const [activeTab, setActiveTab] = useState('params');

  // Load API keys from localStorage on mount
  useEffect(() => {
    const savedGeminiKey = localStorage.getItem('geminiApiKey') || '';
    const savedAnthropicKey = localStorage.getItem('anthropicApiKey') || '';
    
    setGeminiApiKey(savedGeminiKey);
    setAnthropicApiKey(savedAnthropicKey);
    setTempSettings(settings);
  }, [settings, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Save API keys to localStorage and make them available globally
    if (geminiApiKey) {
      localStorage.setItem('geminiApiKey', geminiApiKey);
      window.REACT_APP_GEMINI_API_KEY = geminiApiKey;
    }
    
    if (anthropicApiKey) {
      localStorage.setItem('anthropicApiKey', anthropicApiKey);
      window.REACT_APP_ANTHROPIC_API_KEY = anthropicApiKey;
    }
    
    // Update settings
    setSettings({
      systemPrompt: tempSettings.systemPrompt,
      supervisorMode: tempSettings.supervisorMode,
      enhancedDocProcessing: tempSettings.enhancedDocProcessing !== false
    });
    
    console.log('Settings saved:', {
      supervisorMode: tempSettings.supervisorMode,
      systemPrompt: tempSettings.systemPrompt,
      geminiApiKey: geminiApiKey ? `${geminiApiKey.substring(0, 10)}...` : 'not set',
      anthropicApiKey: anthropicApiKey ? `${anthropicApiKey.substring(0, 10)}...` : 'not set'
    });
    
    onClose();
  };



  if (!isOpen) return null;

  return (
    <ModalBackground onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FontAwesomeIcon icon={faSliders} />
            הגדרות מערכת
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </ModalHeader>

        <TabContainer>
          <Tab
            className={activeTab === 'params' ? 'active' : ''}
            onClick={() => setActiveTab('params')}
          >
            <FontAwesomeIcon icon={faSliders} />
            הגדרות כלליות
          </Tab>
          <Tab
            className={activeTab === 'api' ? 'active' : ''}
            onClick={() => setActiveTab('api')}
          >
            <FontAwesomeIcon icon={faKey} />
            מפתחות API
          </Tab>
        </TabContainer>

        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <TabContent active={activeTab === 'params'}>
              <SectionTitle>הגדרות כלליות</SectionTitle>

              <ToggleContainer>
                <span>מצב Supervisor (ישיבת צוות):</span>
                <ToggleSwitch>
                  <ToggleInput
                    type="checkbox"
                    checked={!!tempSettings.supervisorMode}
                    onChange={(e) => setTempSettings({ ...tempSettings, supervisorMode: e.target.checked })}
                  />
                  <ToggleSlider />
                </ToggleSwitch>
              </ToggleContainer>

              <ToggleContainer>
                <span>Enable Enhanced Document Processing</span>
                <ToggleSwitch>
                  <ToggleInput
                    type="checkbox"
                    checked={tempSettings.enhancedDocProcessing !== false}
                    onChange={(e) => setTempSettings({ ...tempSettings, enhancedDocProcessing: e.target.checked })}
                  />
                  <ToggleSlider />
                </ToggleSwitch>
              </ToggleContainer>

              <Label>
                הנחיית מערכת ברירת מחדל:
                <TextArea
                  value={tempSettings.systemPrompt}
                  onChange={(e) => setTempSettings({ ...tempSettings, systemPrompt: e.target.value })}
                  placeholder="הכנס הנחיית מערכת ברירת מחדל..."
                />
              </Label>

              <div style={{ marginTop: 'var(--space-6)', padding: 'var(--space-4)', background: '#f8f9fa', borderRadius: 'var(--radius-lg)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  <strong>💡 טיפ:</strong> הגדרות מתקדמות כמו מודל AI, מקסימום טוקנים וטמפרטורה ניתנות להגדרה
                  באופן אישי עבור כל דמות בנפרד בחלק "עריכת דמות".
                </p>
              </div>
            </TabContent>

            <TabContent active={activeTab === 'api'}>
              <SectionTitle>מפתחות API</SectionTitle>

              <Label>
                מפתח Gemini API:
                <Input
                  type="password"
                  value={geminiApiKey}
                  onChange={(e) => setGeminiApiKey(e.target.value)}
                  placeholder="הכנס מפתח Gemini API..."
                />
              </Label>

              <Label>
                מפתח Anthropic API:
                <Input
                  type="password"
                  value={anthropicApiKey}
                  onChange={(e) => setAnthropicApiKey(e.target.value)}
                  placeholder="הכנס מפתח Anthropic API..."
                />
              </Label>
            </TabContent>
          </Form>
        </ModalBody>

        <ModalFooter>
          <Button type="button" onClick={handleSubmit}>שמור הגדרות</Button>
        </ModalFooter>
      </ModalContent>
    </ModalBackground>
  );
}

export default SettingsModal;
